'use client';
/*
 * Documentation:
 * BasicCard05 — https://app.subframe.com/6fc7f5fbb098/library?component=BasicCard05_64f3a2c3-290a-479b-9077-2230f736fd01
 * Icon Button — https://app.subframe.com/6fc7f5fbb098/library?component=Icon+Button_af9405b1-8c54-4e01-9786-5aad308224f6
 */

import React from 'react';
import { FeatherMoreVertical } from '@subframe/core';
import * as SubframeCore from '@subframe/core';
import * as SubframeUtils from '../utils';
import { IconButton } from '../common/IconButton';

interface BasicCard05RootProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  badge?: React.ReactNode;
  dropdownMenu?: React.ReactNode;
  hideMenu?: boolean;
  description?: React.ReactNode;
  usageInfo?: React.ReactNode;
  className?: string;
}

const BasicCard05Root = React.forwardRef<HTMLDivElement, BasicCard05RootProps>(
  function BasicCard05Root(
    {
      title,
      subtitle,
      badge,
      dropdownMenu,
      hideMenu = false,
      description,
      usageInfo,
      className,
      ...otherProps
    }: BasicCard05RootProps,
    ref,
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          'group/64f3a2c3 bg-default-background hover:bg-brand-50 flex w-full cursor-pointer flex-col items-start gap-4 rounded-[16px] border border-solid border-neutral-200 px-4 py-4',
          className,
        )}
        ref={ref}
        {...otherProps}
      >
        <div className="flex w-full items-center gap-4">
          <div className="flex shrink-0 grow basis-0 flex-col items-start gap-1">
            {title ? (
              <span className="text-heading-3 font-heading-3 text-default-font w-full">
                {title}
              </span>
            ) : null}
            <div className="flex items-center gap-2">
              {subtitle ? (
                <span className="text-caption font-caption text-default-font">{subtitle}</span>
              ) : null}
              {badge ? <div className="flex items-center gap-2">{badge}</div> : null}
            </div>
          </div>
          {!hideMenu && (
            <SubframeCore.DropdownMenu.Root>
              <SubframeCore.DropdownMenu.Trigger asChild={true}>
                <IconButton icon={<FeatherMoreVertical />} />
              </SubframeCore.DropdownMenu.Trigger>
              <SubframeCore.DropdownMenu.Portal>
                <SubframeCore.DropdownMenu.Content
                  side="bottom"
                  align="end"
                  sideOffset={4}
                  asChild={true}
                >
                  {dropdownMenu ? (
                    <div className="flex items-start justify-between">{dropdownMenu}</div>
                  ) : null}
                </SubframeCore.DropdownMenu.Content>
              </SubframeCore.DropdownMenu.Portal>
            </SubframeCore.DropdownMenu.Root>
          )}
        </div>
        <div className="flex w-full flex-col items-start gap-4">
          {description ? (
            <span className="text-body font-body text-default-font line-clamp-2">
              {description}
            </span>
          ) : null}
          {usageInfo ? (
            <span className="text-caption font-caption text-subtext-color">{usageInfo}</span>
          ) : null}
        </div>
      </div>
    );
  },
);

export const BasicCard05 = BasicCard05Root;
